import React, { useMemo, useState, useEffect } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { useAuth } from '../../context/authContext';
import CardCheck from '../components/Login/CardCheck';
import { startAuthentication } from '@simplewebauthn/browser';
import useWebAuthnApi from '../../hooks/useWebAuthnApi';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function AuthenticationModal({
  isOpen,
  onClose,
  eligibleUsers = [],
  requiredRights = [],
  onAuthenticationSuccess,
  onCancel,
  isCounterDevice = true,
  displayInfo = null,
}) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const authCtx = useAuth();
  const { webauthnStepupStart, webauthnStepupFinish } = useWebAuthnApi();
  const [showPasswordLogin, setShowPasswordLogin] = useState(!isCounterDevice); // Show password login directly for non-counter devices
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showNoTokenModal, setShowNoTokenModal] = useState(false);
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [showCheckmarkAnimation, setShowCheckmarkAnimation] = useState(false);
  const [showCrossAnimation, setShowCrossAnimation] = useState(false);

  const infoPaperSx = useMemo(
    () => ({
      p: 1.5,
      mb: 1,
      backgroundColor: isLight ? theme.palette.grey[100] : alpha(theme.palette.common.white, 0.1),
      border: isLight
        ? `1px solid ${theme.palette.divider}`
        : `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
      borderRadius: 2,
    }),
    [theme, isLight]
  );

  const infoTextPrimary = isLight ? theme.palette.text.primary : alpha(theme.palette.common.white, 0.9);
  const chipOutlinedOnInfo = useMemo(
    () => ({
      color: isLight ? theme.palette.text.primary : alpha(theme.palette.common.white, 0.95),
      borderColor: isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.35),
      fontFamily: 'monospace',
      maxWidth: '100%',
    }),
    [theme, isLight]
  );

  const normalizedRequiredRights = useMemo(() => {
    const arr = Array.isArray(requiredRights)
      ? requiredRights
      : requiredRights
        ? [requiredRights]
        : [];
    const normalized = arr.map(r => String(r).trim()).filter(Boolean);
    return Array.from(new Set(normalized)).sort((a, b) => a.localeCompare(b));
  }, [requiredRights]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setShowPasswordLogin(!isCounterDevice); // Always show password login for non-counter devices
      setError('');
      setUsername('');
      setPassword('');
    }
  }, [isOpen, isCounterDevice]);

  const handleRfidSuccess = (cardId, intermediateTokenData) => {
    // If successful, call the success callback with card ID
    onAuthenticationSuccess && onAuthenticationSuccess(cardId);
    onClose();
  };

  const handlePasswordSuccess = (event, username, password) => {
    // Call success callback with username and password for additional authentication
    onAuthenticationSuccess && onAuthenticationSuccess(null, username, password);
    onClose();
  };

  const handlePasskeySuccess = async () => {
    setError('');
    setIsProcessing(true);
    try {
      const startResponse = await webauthnStepupStart({
        requiredRights: normalizedRequiredRights,
      });
      const options = startResponse?.options;
      const challenge = startResponse?.challenge;
      if (!options || !challenge) {
        throw new Error('Ungueltige Passkey-Antwort vom Server.');
      }

      const credential = await startAuthentication({ optionsJSON: options });
      const finishResponse = await webauthnStepupFinish({ challenge, credential });
      const additionalAuthToken = finishResponse?.additionalAuthToken;
      if (!additionalAuthToken) {
        throw new Error('Passkey Step-up Token wurde nicht erstellt.');
      }

      onAuthenticationSuccess && onAuthenticationSuccess(null, null, null, additionalAuthToken);
      onClose();
    } catch (err) {
      console.error('Error during Passkey step-up authentication:', err);
      setError(
        err?.responseData?.message ||
          err?.message ||
          'Fehler bei der Passkey-Authentifizierung.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const getCardText = () => {
    return 'Bitte Mitarbeiterausweis an das Lesegerät halten';
  };

  const renderInfo = () => {
    const hasDisplayInfo = Boolean(displayInfo && Object.keys(displayInfo).length > 0);
    const hasRights = Boolean(normalizedRequiredRights && normalizedRequiredRights.length > 0);

    if (!hasDisplayInfo && !hasRights) {
      return null;
    }

    return (
      <Paper elevation={2} sx={infoPaperSx}>
        {hasDisplayInfo && (
          <>
            <Typography variant='h6' sx={{ color: 'text.primary', mb: 0.5, fontWeight: 'bold' }}>
              {displayInfo.Typ || 'Anfrage'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
              {Object.entries(displayInfo).map(([key, value]) => {
                if (key === 'Typ') return null; // Skip type as it's already shown as header
                return (
                  <Typography key={key} variant='body2' sx={{ color: infoTextPrimary }}>
                    <strong>{key}:</strong> {value}
                  </Typography>
                );
              })}
            </Box>
          </>
        )}

        {hasRights && (
          <Box sx={{ mt: hasDisplayInfo ? 1.25 : 0 }}>
            <Typography variant='subtitle2' sx={{ color: infoTextPrimary, mb: 0.75 }}>
              Fehlendes Recht{normalizedRequiredRights.length === 1 ? '' : 'e'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {normalizedRequiredRights.map(right => (
                <Chip key={right} label={right} size='small' variant='outlined' sx={chipOutlinedOnInfo} />
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    );
  };

  const renderRequiredRights = () => {
    if (!normalizedRequiredRights || normalizedRequiredRights.length === 0) return null;

    return (
      <Paper elevation={2} sx={infoPaperSx}>
        <Typography variant='subtitle2' sx={{ color: infoTextPrimary, mb: 0.75 }}>
          Fehlendes Recht{normalizedRequiredRights.length === 1 ? '' : 'e'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {normalizedRequiredRights.map(right => (
            <Chip key={right} label={right} size='small' variant='outlined' sx={chipOutlinedOnInfo} />
          ))}
        </Box>
      </Paper>
    );
  };

  const renderHeader = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Typography
          variant='h5'
          component='h2'
          align='center'
          sx={{ color: 'text.primary', mb: 0.5 }}
        >
          Authentifizierung erforderlich
        </Typography>
        {renderInfo()}
        {/* If we have no displayInfo box, show rights in its own box */}
        {(!displayInfo || Object.keys(displayInfo).length === 0) && renderRequiredRights()}
      </Box>
    );
  };

  if (!isOpen) return null;

  return (
    <Box sx={viewRootTextPrimarySx}>
    <CardCheck
      // Core props
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onCancel}
      // RFID handling
      onRfidSuccess={handleRfidSuccess}
      onRfidError={err => {
        console.error('Error in handleRfidInput:', err);
        setError(err.message || 'Fehler beim RFID-Login');
      }}
      intermediateTokens={authCtx.intermediateTokens}
      eligibleUsers={eligibleUsers}
      // Password handling
      onPasswordSuccess={handlePasswordSuccess}
      onPasswordError={error => {
        console.error('Error in password authentication:', error);
        setError('Fehler bei der Authentifizierung: ' + error.message);
      }}
      // UI customization
      title={null}
      cardText={getCardText()}
      showPasswordLogin={showPasswordLogin}
      setShowPasswordLogin={setShowPasswordLogin}
      showNoEligibleUsersMessage={true}
      disableButtonsWhenNoEligibleUsers={true}
      // Styling
      darkTheme={true}
      showLogo={false}
      // Button customization
      authenticateButtonText='Authentifizieren'
      showPasskeyLoginButton={true}
      onPasskeyLogin={handlePasskeySuccess}
      passkeyLoginButtonText='Mit Passkey anmelden'
      showBackButton={false}
      // Modal customization
      showNoTokenModal={showNoTokenModal}
      setShowNoTokenModal={setShowNoTokenModal}
      showFirstLoginModal={showFirstLoginModal}
      setShowFirstLoginModal={setShowFirstLoginModal}
      // Error handling
      error={error}
      setError={setError}
      isProcessing={isProcessing}
      setIsProcessing={setIsProcessing}
      // Form state
      username={username}
      setUsername={setUsername}
      password={password}
      setPassword={setPassword}
      // Animation states
      showCheckmarkAnimation={showCheckmarkAnimation}
      setShowCheckmarkAnimation={setShowCheckmarkAnimation}
      showCrossAnimation={showCrossAnimation}
      setShowCrossAnimation={setShowCrossAnimation}
      // Device type handling
      isCounterDevice={isCounterDevice}
    >
      {renderHeader()}
    </CardCheck>
    </Box>
  );
}

export default AuthenticationModal;
